/**
 * A small collection of useful scrolling utility functions.
 */

/**
 * Get the height of the entire document.
 * Based on http://stackoverflow.com/a/1147768/2571482
 */
export const getDocumentHeight = function (): number {
    const html = document.documentElement;
    const body = document.body;

    if (!html || !body) {
        return 0;
    }

    return Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight,
    );
};

/**
 * Get the vertical offset of the bottom of an element
 * relative to the top of the page.
 *
 * @param {Node} element The element to measure.
 */
export const getOffsetBottom = function (element?: HTMLElement | null): number {
    if (!element) {
        return 0;
    }
    const box = element.getBoundingClientRect();
    return (
        // The distance from the bottom of the element to the top of the screen
        // Old IE: Adjust for vertical space that's not actually usable
        box.bottom +
        // The distance from the top of the screen to the top of the page
        window.pageYOffset -
        document.documentElement.clientTop
    );
};

/**
 * Get the vertical offset of the top of an element
 * relative to the top of the page.
 *
 * @param {Node} element The element to measure.
 */
export const getOffsetTop = function (element?: HTMLElement | null): number {
    if (!element) {
        return 0;
    }
    const box = element.getBoundingClientRect();
    return (
        // The distance from the top of the element to the top of the screen
        // Old IE: Adjust for vertical space that's not actually usable
        box.top +
        // The distance from the top of the screen to the top of the page
        window.pageYOffset -
        document.documentElement.clientTop
    );
};

/**
 * Smoothly scroll to a given element with easing in out.
 *
 * @param {Node} element The element to scroll to.
 * @param {number} duration How long the scroll should take.
 * @param {function=} callback An optional callback to fire on scroll end.
 */
export const scrollToElement = (
    element: HTMLElement,
    duration?: number,
    callback?: any,
): void => {
    if (!element) {
        return;
    }
    const offset = getOffsetTop(element);
    scrollToOffset(offset, duration || 0, callback);
};

/**
 * Smoothly scroll to a given offset with easing in out.
 *
 * @param {number} offset The vertical offset in px to scroll to.
 * @param {number} duration How long the scroll should take.
 * @param {function=} callback An optional callback to fire on scroll end.
 */
export const scrollToOffset = (
    offset: number,
    duration: number,
    callback?: () => unknown,
): void => {
    scrollElementToOffset(window, offset, duration, callback);
};

// Polyfill scrollTo if it doesn't exist
if (typeof HTMLElement !== "undefined" && !HTMLElement.prototype.scrollTo) {
    // @ts-expect-error - TS2322 - Type '(this: any, left: number, top: number) => void' is not assignable to type '{ (options?: ScrollToOptions | undefined): void; (x: number, y: number): void; }'.
    HTMLElement.prototype.scrollTo = function (
        this: any,
        left: number,
        top: number,
    ) {
        this.scrollLeft = left;
        this.scrollTop = top;
    };
}

/**
 * Handles applying a given scroll object to either the window object,
 * or an HTML element.
 * NOTE: SIDE EFFECTS. This method touches the DOM.
 */
export const applyUniversalScrollOffset = (
    elementOrWindow: HTMLElement | WindowProxy,
    amount: number,
) => {
    // Window objects need to be scrolled by using the `.scrollTo()` function.
    // Elements in MODERN browsers also have this method, but a safer x-browser
    // solution is to use the `.scrollTop` property setter.
    if (typeof elementOrWindow.scrollTo === "function") {
        elementOrWindow.scrollTo(0, amount);
    } else if ("scrollTop" in elementOrWindow) {
        elementOrWindow.scrollTop = amount;
    }
};

/**
 * Smoothly scroll the given element to the given offset.
 *
 * This assumes that element.scrollHeight > element.clientHeight.
 */
export const scrollElementToOffset = (
    elementOrWindow: HTMLElement | WindowProxy,
    offset: number,
    duration = 0,
    callback?: any,
) => {
    const startPosition =
        elementOrWindow === window
            ? window.pageYOffset
            : // @ts-expect-error - TS2551 - Property 'scrollTop' does not exist on type 'Window | HTMLElement'. Did you mean 'scrollTo'?
              elementOrWindow.scrollTop;
    const endPosition = offset;
    const scrollDistance = endPosition - startPosition;

    // Aim for ~60 FPS
    const tickLength = 17;
    const numberOfSteps = duration / tickLength;
    let step = 0;

    // TODO (josh): Rewrite this using window.requestAnimationFrame
    /* eslint-disable-next-line no-restricted-syntax
       -- difficult to use Wonder Blocks Timing API here */
    const scrollInterval = setInterval(function () {
        if (step < numberOfSteps) {
            const x = step / numberOfSteps; // Domain: [0, 1]
            const y = (-Math.cos(Math.PI * x) + 1) / 2; // Range: [0, 1]

            const newPosition = startPosition + scrollDistance * y;
            applyUniversalScrollOffset(elementOrWindow, newPosition);

            step += 1;
        } else {
            // Account for non-integer numberOfSteps
            applyUniversalScrollOffset(elementOrWindow, endPosition);

            /* eslint-disable-next-line no-restricted-syntax
               -- difficult to use Wonder Blocks Timing API here */
            clearInterval(scrollInterval);
            callback && callback();
        }
    }, tickLength);
};

/**
 * Returns the closest ancestor that is scrollable.
 *
 * If the container is document.body we return window b/c scrollTo() doesn't
 * work on document.body in some browsers.
 */
export const getScrollContainer = (element?: Element | null): any | Element => {
    if (element === document.body) {
        return window;
    }
    if (element == null) {
        return window;
    }
    if (element.scrollHeight > element.clientHeight) {
        return element;
    }
    return getScrollContainer(element.parentElement);
};

export const SCROLL_ANIMATION_DURATION = 750; // ms

/**
 * Scroll the given element into view.
 *
 * This works with elements inside scroll containers, but may fail in scenarios
 * involving multiple nested scroll containers.
 */
export const scrollElementIntoView = (
    element: Element,
    callback?: any,
    options?: {duration: number},
) => {
    // Often times we scroll an element into with in reaction to some change in
    // the DOM.  Since we need to measure things, we wait a tick for the DOM to
    // settle before computing the distance to scroll and beginning the scroll
    // animation.
    /* eslint-disable-next-line no-restricted-syntax
       -- difficult to use Wonder Blocks Timing API here */
    setTimeout(() => {
        const scrollContainer = getScrollContainer(element);

        const childBounds = element.getBoundingClientRect();
        const containerBounds =
            scrollContainer === window
                ? {
                      top: 0,
                      bottom: window.innerHeight,
                  }
                : scrollContainer.getBoundingClientRect();

        let offset = 0;
        const currentScrollTop =
            scrollContainer === window
                ? window.scrollY
                : scrollContainer.scrollTop;

        if (childBounds.bottom > containerBounds.bottom) {
            let distanceToScroll = childBounds.bottom - containerBounds.bottom;

            // Ensure that we see the top of the component we're scrolling.
            if (childBounds.top + distanceToScroll < containerBounds.top) {
                distanceToScroll = childBounds.top - containerBounds.top;
            }

            offset = currentScrollTop + distanceToScroll;
        } else if (childBounds.top < containerBounds.top) {
            const distanceToScroll = childBounds.top - containerBounds.top;

            offset = currentScrollTop + distanceToScroll;
        } else {
            // If the child is neither off the bottom or above the top... don't
            // actually move anywhere. The above calculations fail when the
            // element is entirely on screen. Call the callback as if we did
            // move and return.
            callback?.();
            return;
        }

        scrollElementToOffset(
            scrollContainer,
            offset,
            options?.duration ?? SCROLL_ANIMATION_DURATION,
            callback,
        );
    }, 0);
};

// Note (Third): This was moved to content-library at the request of FE infrastructure,
// subsequently it was moved here to be used by the classroom app as well.
// We would like to eventually move this directly into perseus.
/**
 * Scrolls an element into view while taking into account the on-screen keypad.
 *
 * TODO(kevinb): merge this with scrollElementIntoView by adding a parameter to
 * that function that accepts an array of elements that might obscure the element
 * to be scrolled into view.  This is easier said than done as there are some
 * assumption that we can make about the keypad element that we can't make when
 * handling the general case.
 */
export const scrollElementIntoViewWithKeypad = (
    // The focused element that we want to scroll into view
    focusedElement: HTMLElement | null | undefined,
    // The scrollable container that contains the focused element
    scrollableElement: HTMLElement | null | undefined,
    // The keypad element that might obscure the focused element
    keypadHeight: number | undefined,
    // The duration of the scroll animation
    duration: number,
) => {
    if (focusedElement && keypadHeight && scrollableElement) {
        const scrollTop = scrollableElement.scrollTop;

        const containerTop = scrollableElement.getBoundingClientRect().top;
        const containerBottom =
            scrollableElement.getBoundingClientRect().bottom;

        // The keypad might not be visible yet so we have to wait for it to
        // become visible before we can compute its height.
        // eslint-disable-next-line no-restricted-syntax -- not sure why we don't use the Wonder Blocks Timing API here
        setTimeout(() => {
            // The keypad is in the process of animating upward so we have to
            // compute where it's top will once the animation completes.
            const keypadTop = containerBottom - keypadHeight;
            const focusedElementTop =
                focusedElement.getBoundingClientRect().top;
            const focusedElementBottom =
                focusedElement.getBoundingClientRect().bottom;
            const paddingAroundinput = 16;

            const scrollBy = (delta: number) =>
                scrollElementToOffset(
                    scrollableElement,
                    scrollTop + delta,
                    duration,
                );

            // The on-screen keypad obscures the bottom part of the container
            // so the top of the keypad is actually the bottom of the visible
            // region of the scrollableElement.
            if (focusedElementBottom + paddingAroundinput > keypadTop) {
                // Scrolling Down
                scrollBy(focusedElementBottom + paddingAroundinput - keypadTop);
            } else if (focusedElementTop - paddingAroundinput < containerTop) {
                // Scrolling Up
                scrollBy(focusedElementTop - paddingAroundinput - containerTop);
            }
        }, 0);
    }
};
